// @ts-nocheck
/**
 * Template V2 — predefined edit forms (callable host modal layouts).
 *
 * Sibling contract to ElementLayout[] (canvas tiles) and ElementOverviewPresentation.
 * Forms are opened via builtin/script `openEditForm`; they do not replace Edit Entry.
 */

import type { DataRef, ValueSource } from './binding';
import type { LocalizedText } from './metadata';

/** Row kinds that may appear in an edit form (all optional; order is free). */
export const EDIT_FORM_ROW_KINDS = [
  'chrome',
  'groupTitle',
  'groupSubtitle',
  'propertyTitle',
  'propertySubtitle',
  'field',
  'images',
  'documents',
  'spacer',
  'divider',
] as const;

export type EditFormRowKind = (typeof EDIT_FORM_ROW_KINDS)[number];

/** Visibility rule — when false/hidden the row is omitted (column reflows). */
export interface EditFormVisibility {
  visible: ValueSource<boolean>;
}

/** Authored text: static LocalizedText or a resolved ValueSource. */
export type EditFormText = LocalizedText | ValueSource<string>;

/** Modal chrome: optional close (X), title, accept (check). */
export interface EditFormChromeRow {
  id: string;
  kind: 'chrome';
  title?: EditFormText;
  showClose?: boolean;
  showAccept?: boolean;
  visibility?: EditFormVisibility;
}

/** Section / property label rows. */
export interface EditFormLabelRow {
  id: string;
  kind: 'groupTitle' | 'groupSubtitle' | 'propertyTitle' | 'propertySubtitle';
  text: EditFormText;
  visibility?: EditFormVisibility;
}

/**
 * Editable field bound to an item field or category property.
 * Host picks the Edit Entry widget from the target’s type metadata.
 */
export interface EditFormFieldRow {
  id: string;
  kind: 'field';
  /** Must be `itemField` or `categoryProperty`. */
  target: DataRef;
  /**
   * When true, host renders an automatic label from the target’s metadata
   * (property/field name). Default false — authors place propertyTitle rows instead.
   */
  showLabel?: boolean;
  visibility?: EditFormVisibility;
}

/**
 * Item image gallery editor (same host widget as Edit Entry’s images section).
 * Mutates item imageUris / imageTitles / imageDates / defaultThumbnailIndex.
 */
export interface EditFormImagesRow {
  id: string;
  kind: 'images';
  /** Default true — show default-thumbnail selector. */
  showDefaultThumbnailSelector?: boolean;
  visibility?: EditFormVisibility;
}

/**
 * Item document attachment editor (same host widget as Edit Entry’s documents section).
 * Mutates item documents / documentNames / documentDates / documentSizes / documentTypes.
 */
export interface EditFormDocumentsRow {
  id: string;
  kind: 'documents';
  visibility?: EditFormVisibility;
}

/** Vertical gap (dp/px). */
export interface EditFormSpacerRow {
  id: string;
  kind: 'spacer';
  height: number;
  visibility?: EditFormVisibility;
}

/** Horizontal rule. */
export interface EditFormDividerRow {
  id: string;
  kind: 'divider';
  thickness: number;
  visibility?: EditFormVisibility;
}

export type EditFormRow =
  | EditFormChromeRow
  | EditFormLabelRow
  | EditFormFieldRow
  | EditFormImagesRow
  | EditFormDocumentsRow
  | EditFormSpacerRow
  | EditFormDividerRow;

/** One named form attached to an element (opened by id). */
export interface EditFormDefinition {
  id: string;
  /** Graph / picker label. */
  displayName?: LocalizedText;
  /** Author order = render order (top → bottom). */
  rows: EditFormRow[];
}
